export interface TimedItem<T> {
    item: T;
    /** Interval start, in any consistent unit (minutes, timestamps…). */
    start: number;
    /** Interval end, same unit as start. */
    end: number;
}

export interface PositionedItem<T> extends TimedItem<T> {
    /** Zero-based column inside the overlap cluster. */
    column: number;
    /** Total columns of the overlap cluster. */
    columns: number;
}

/**
 * Assign a column/columns pair to each interval so overlapping items share
 * the width of their overlap cluster, calendar-style. Items of a cluster
 * are transitively overlapping; non-overlapping items keep the full width.
 */
export const layoutOverlaps = <T>(items: TimedItem<T>[]): PositionedItem<T>[] => {
    const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);

    const positioned: PositionedItem<T>[] = [];
    let cluster: PositionedItem<T>[] = [];
    let columnEnds: number[] = [];
    let clusterEnd = 0;

    const flushCluster = () => {
        cluster.forEach((entry) => {
            entry.columns = columnEnds.length;
        });

        positioned.push(...cluster);
        cluster = [];
        columnEnds = [];
    };

    for (const timed of sorted) {
        if (cluster.length && timed.start >= clusterEnd) {
            flushCluster();
        }

        let column = columnEnds.findIndex((end) => end <= timed.start);

        if (column === -1) {
            column = columnEnds.length;
            columnEnds.push(0);
        }

        columnEnds[column] = timed.end;
        clusterEnd = Math.max(clusterEnd, timed.end);
        cluster.push({...timed, column, columns: 0});
    }

    flushCluster();

    return positioned;
};
